import { AlertTriangle, Info, RefreshCw, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { useTableSort } from "@/components/sortable-table-head";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProjectServicesSection } from "@/features/home/project-services";
import {
  SuspendDialog,
  type SuspensionTarget,
} from "@/features/home/project-services/suspend-dialog";
import {
  type AiServiceRow,
  type AiServicesOverview,
  type SvcService,
  useAdminAiServices,
} from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { formatUtcDate } from "@/lib/format";
import { logger } from "@/lib/logger";
import { SERVICE_LABEL, SVC_SERVICES } from "@/lib/services-gateway";

import { KillSwitchDialog, type KillSwitchTarget } from "./kill-switch-dialog";
import { OverviewStats, ServiceCards } from "./overview-cards";
import { ROW_FILTERS, type RowFilter, rowMatches, type SortColumn, sortRows } from "./rows";
import { type RowAction, ServicesTable } from "./services-table";

/** The project whose Services section is open in the Manage panel. */
interface ManagedProject {
  projectId: number;
  projectName: string;
}

/**
 * Admin → AI services: every services gateway project's use and spend this
 * month, what is near or over budget, what bypasses the gateway, and the
 * reversible stops -- one service of a project, a whole project, or a
 * service for every project (the kill switch). Per row, the project's own
 * Services section (tier, budget, rotate, revoke, re-issue, the models' mode)
 * opens in a panel. Shown only with the services gateway on.
 */
export function AdminAiServicesPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = useAdminAiServices();
  const [filter, setFilter] = useState<RowFilter>("all");
  const [service, setService] = useState<SvcService | "all">("all");
  const [search, setSearch] = useState("");
  const { sortBy, sortDir, onSort } = useTableSort({ sortBy: "used", sortDir: "desc" });
  const [suspension, setSuspension] = useState<SuspensionTarget | null>(null);
  const [killSwitch, setKillSwitch] = useState<KillSwitchTarget | null>(null);
  const [managed, setManaged] = useState<ManagedProject | null>(null);

  const rows = useMemo(
    () =>
      sortRows(
        (data?.rows ?? []).filter((row) => rowMatches(row, filter, service, search)),
        sortBy as SortColumn | null,
        sortDir,
      ),
    [data, filter, service, search, sortBy, sortDir],
  );

  const handleAction = (action: RowAction) => {
    const { row } = action;
    if (row.project_id == null) {
      return;
    }
    logger.debug("[ai-services] row action", action.kind, row.project_slug, row.service);
    if (action.kind === "manage") {
      setManaged({ projectId: row.project_id, projectName: row.project_name });
      return;
    }
    const projectRows = (data?.rows ?? []).filter((other) => other.project_id === row.project_id);
    setSuspension({
      projectId: row.project_id,
      projectName: row.project_name,
      service: action.kind === "service" ? row.service : null,
      suspend: action.suspend,
      offGateway:
        action.kind === "project"
          ? projectRows.filter((other) => other.off_gateway).map((other) => other.service)
          : [],
    });
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-6 py-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-primary text-2xl font-normal">AI services</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Use and spend of every services gateway project (dev)
            {data?.registry ? ` since ${formatUtcDate(data.registry.month_start)} (UTC month)` : ""}
          </p>
        </div>
        <Button variant="outline" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw className={isFetching ? "mr-2 size-4 animate-spin" : "mr-2 size-4"} />
          Refresh
        </Button>
      </div>

      <div className="border-border bg-muted/40 flex items-start gap-2 rounded-lg border p-3">
        <Info className="text-muted-foreground mt-0.5 size-4 shrink-0" />
        <p className="text-muted-foreground text-xs">
          Suspending stops a project&apos;s calls within about a minute without revoking a key:
          resuming brings the same keys back, and nothing is redeployed. Owners see the reason you
          give. Use is metered every 15 minutes. To change a key, a tier, a budget or how the models
          are reached, open a row&apos;s <em>Keys, limits and mode</em>; budget requests are in{" "}
          <Link to="/admin/service-budget-requests" className="text-primary hover:underline">
            Budget Requests
          </Link>
          .
        </p>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground py-16 text-center text-sm">Loading AI services…</div>
      ) : isError || !data ? (
        <div className="text-destructive py-16 text-center text-sm">
          {readApiErrorMessage(error, "Could not load the AI services.")}
        </div>
      ) : (
        <Overview
          data={data}
          rows={rows}
          table={{ sortBy, sortDir, onSort }}
          filters={{ filter, setFilter, service, setService, search, setSearch }}
          onAction={handleAction}
          onKillSwitch={setKillSwitch}
        />
      )}

      <SuspendDialog
        target={suspension}
        onOpenChange={(open) => {
          if (!open) {
            setSuspension(null);
          }
        }}
      />
      <KillSwitchDialog
        target={killSwitch}
        onOpenChange={(open) => {
          if (!open) {
            setKillSwitch(null);
          }
        }}
      />
      <ManagePanel project={managed} onClose={() => setManaged(null)} />
    </div>
  );
}

interface OverviewProps {
  data: AiServicesOverview;
  rows: AiServiceRow[];
  table: Pick<React.ComponentProps<typeof ServicesTable>, "sortBy" | "sortDir" | "onSort">;
  filters: {
    filter: RowFilter;
    setFilter: (filter: RowFilter) => void;
    service: SvcService | "all";
    setService: (service: SvcService | "all") => void;
    search: string;
    setSearch: (search: string) => void;
  };
  onAction: (action: RowAction) => void;
  onKillSwitch: (target: KillSwitchTarget) => void;
}

function Overview({ data, rows, table, filters, onAction, onKillSwitch }: OverviewProps) {
  return (
    <>
      {data.registry_error && (
        <p className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border p-3 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          The gateway&apos;s key registry did not fully answer: {data.registry_error} What it could
          not say is left out; Scaffold UI&apos;s own counts are complete.
        </p>
      )}

      <OverviewStats overview={data} />
      <ServiceCards overview={data} onKillSwitch={onKillSwitch} />

      {data.without_keys && data.without_keys.length > 0 && (
        <div className="border-border rounded-lg border p-3 text-sm">
          <p className="font-medium">
            {data.without_keys.length} gateway project
            {data.without_keys.length === 1 ? " has" : "s have"} no key in the registry
          </p>
          <ul className="text-muted-foreground mt-1 space-y-0.5 text-xs">
            {data.without_keys.map((project) => (
              <li key={project.project_id}>
                <Link
                  to={`/home?project=${project.project_id}&tab=details`}
                  className="text-primary hover:underline"
                >
                  {project.project_name}
                </Link>
                {project.warning ? ` — ${project.warning}` : " — re-issue its keys."}
              </li>
            ))}
          </ul>
        </div>
      )}
      {data.truncated && (
        <p className="text-muted-foreground text-xs">
          The table shows the first projects only; the totals above count every project.
        </p>
      )}

      <div className="bg-card border-border overflow-hidden rounded-lg border">
        <div className="border-border flex flex-wrap items-center gap-3 border-b px-4 py-3">
          <div className="relative max-w-sm min-w-48 flex-1">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              placeholder="Search by project or owner..."
              value={filters.search}
              onChange={(e) => filters.setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={filters.filter}
            onValueChange={(value) => filters.setFilter(value as RowFilter)}
          >
            <SelectTrigger className="w-56" aria-label="Show">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROW_FILTERS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.service}
            onValueChange={(value) => filters.setService(value as SvcService | "all")}
          >
            <SelectTrigger className="w-48" aria-label="Service">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Every service</SelectItem>
              {SVC_SERVICES.map((service) => (
                <SelectItem key={service} value={service}>
                  {SERVICE_LABEL[service]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-muted-foreground ml-auto text-xs">
            {rows.length} of {data.rows?.length ?? 0} rows
          </p>
        </div>
        <ServicesTable rows={rows} {...table} onAction={onAction} />
      </div>
    </>
  );
}

/** One project's Services section (#111's own controls), opened from a row. */
function ManagePanel({
  project,
  onClose,
}: {
  project: ManagedProject | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={project !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        {project && (
          <>
            <DialogHeader>
              <DialogTitle>{project.projectName}</DialogTitle>
              <DialogDescription>
                Keys, limits, suspension and how the models are reached, in dev.{" "}
                <Link
                  to={`/home?project=${project.projectId}&tab=details`}
                  className="text-primary hover:underline"
                >
                  Open the project
                </Link>
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <ProjectServicesSection
                projectId={project.projectId}
                projectName={project.projectName}
                isAdmin
              />
            </DialogBody>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
